import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [attempts, setAttempts] = useState(0);
  const nav = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (n >= 8) { setStatus("failed"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (n >= 8) { setStatus("failed"); return; }
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="p-12 max-w-2xl mx-auto text-center">
      {status === "polling" && (
        <>
          <Spinner size={48} className="animate-spin mx-auto mb-4 text-primary" />
          <h1 className="font-display text-4xl mb-3">Verifying payment…</h1>
          <p className="text-muted-foreground">Attempt {attempts + 1} of 9</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle size={64} weight="duotone" className="mx-auto mb-4 text-emerald-600" />
          <h1 className="font-display text-5xl mb-3">You're in.</h1>
          <p className="text-muted-foreground mb-8">Your subscription is active. Time to clean some inboxes.</p>
          <Button onClick={() => nav("/dashboard")} className="rounded-none btn-brutalist" data-testid="goto-dashboard">Go to dashboard</Button>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle size={64} weight="duotone" className="mx-auto mb-4 text-destructive" />
          <h1 className="font-display text-4xl mb-3">Payment {status}</h1>
          <p className="text-muted-foreground mb-8">Please try again or contact support.</p>
          <Link to="/pricing"><Button className="rounded-none btn-brutalist">Back to pricing</Button></Link>
        </>
      )}
    </div>
  );
}
